import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { StoreIcon, CalendarIcon, ClockIcon, TagIcon, FilterIcon, SearchIcon, UsersIcon, AwardIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
// Mock data for partnership opportunities
const partnershipOpportunities = [{
  id: '1',
  title: 'Fantasy Series Merchandise Collection',
  description: 'Seeking merchandise partner to create a premium collection for our bestselling fantasy series. Looking for clothing, accessories, and home goods.',
  deadline: '2023-12-15',
  postedDate: '2023-10-01',
  publisherName: 'Epic Realms Publishing',
  publisherLogo: 'https://images.unsplash.com/photo-1560415755-bd80d06eda60?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  categories: ['Clothing', 'Accessories', 'Home Goods'],
  productionVolume: 'Medium (500-2,000 units)',
  projectLength: 'Long-term (6+ months)',
  partnershipType: 'Revenue Share',
  featured: true,
  requirements: 'Experience with fantasy merchandise. Must be able to produce high-quality items with custom designs.'
}, {
  id: '2',
  title: 'Romance Author Branded Merchandise',
  description: 'Bestselling romance author looking for merchandise partner to create branded products for upcoming book tour and online store.',
  deadline: '2023-11-30',
  postedDate: '2023-09-15',
  publisherName: 'Heart & Soul Books',
  publisherLogo: 'https://images.unsplash.com/photo-1516383607781-913a19294fd1?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  categories: ['Clothing', 'Jewelry', 'Candles'],
  productionVolume: 'Small (100-500 units)',
  projectLength: 'Medium-term (3-6 months)',
  partnershipType: 'Wholesale',
  featured: false,
  requirements: 'Experience with author branding. Ability to create romantic, elegant designs.'
}, {
  id: '3',
  title: 'Mystery Series Bookish Accessories',
  description: 'Publisher seeking partner to create themed bookmarks, reading lights, and book sleeves for popular mystery series.',
  deadline: '2023-12-30',
  postedDate: '2023-10-10',
  publisherName: 'Clue Press',
  publisherLogo: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  categories: ['Bookmarks', 'Reading Accessories'],
  productionVolume: 'Medium (500-2,000 units)',
  projectLength: 'Short-term (< 3 months)',
  partnershipType: 'White Label',
  featured: false,
  requirements: 'Experience with book accessories. Ability to incorporate mystery themes into functional products.'
}, {
  id: '4',
  title: 'Literary Festival Merchandise',
  description: 'Annual literary festival looking for merchandise vendor to create exclusive products for attendees and online sales.',
  deadline: '2024-01-15',
  postedDate: '2023-10-05',
  publisherName: 'Wordsmith Events',
  publisherLogo: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  categories: ['Clothing', 'Tote Bags', 'Stationery'],
  productionVolume: 'Large (2,000+ units)',
  projectLength: 'Medium-term (3-6 months)',
  partnershipType: 'Event Partnership',
  featured: true,
  requirements: 'Experience with event merchandise. Ability to handle large volume orders with tight deadlines.'
}, {
  id: '5',
  title: "Children's Book Art Prints",
  description: "Publisher looking for art print vendor to create high-quality prints featuring illustrations from popular children's book series.",
  deadline: '2023-12-10',
  postedDate: '2023-09-25',
  publisherName: 'Little Readers Publishing',
  publisherLogo: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
  categories: ['Art Prints', 'Home Decor'],
  productionVolume: 'Small (100-500 units)',
  projectLength: 'Short-term (< 3 months)',
  partnershipType: 'Licensing',
  featured: false,
  requirements: "Experience with fine art printing. Ability to faithfully reproduce children's book illustrations."
}];
type ProjectLength = 'Any' | 'Short-term (< 3 months)' | 'Medium-term (3-6 months)' | 'Long-term (6+ months)';
type PartnershipType = 'Any' | 'Revenue Share' | 'Wholesale' | 'White Label' | 'Licensing' | 'Event Partnership';
const PartnershipOpportunities = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [projectLength, setProjectLength] = useState<ProjectLength>('Any');
  const [partnershipType, setPartnershipType] = useState<PartnershipType>('Any');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const allCategories = Array.from(new Set(partnershipOpportunities.flatMap(opp => opp.categories)));
  const filteredOpportunities = partnershipOpportunities.filter(opp => {
    const matchesSearch = searchTerm === '' || opp.title.toLowerCase().includes(searchTerm.toLowerCase()) || opp.description.toLowerCase().includes(searchTerm.toLowerCase()) || opp.publisherName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategories = selectedCategories.length === 0 || selectedCategories.some(category => opp.categories.includes(category));
    const matchesProjectLength = projectLength === 'Any' || opp.projectLength === projectLength;
    const matchesPartnershipType = partnershipType === 'Any' || opp.partnershipType === partnershipType;
    const matchesFeatured = !showFeaturedOnly || opp.featured;
    return matchesSearch && matchesCategories && matchesProjectLength && matchesPartnershipType && matchesFeatured;
  });
  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  // Function to format date
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  // Calculate days remaining until deadline
  const getDaysRemaining = (deadline: string) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  return <div className={`min-h-screen ${dk.page}`} data-id="element-20361" data-mp="f91ocmk011lh">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-20362" data-mp="1zuhvts6atphu">
        <div className="mb-8" data-id="element-20363" data-mp="1vs3cz82mqpqk">
          <div className="mb-4" data-id="element-20364" data-mp="1vqs8f61d1dye">
            <BackButton data-id="element-20365" data-mp="1vqjz1o4g4qry" />
          </div>
          <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-20366" data-mp="1qs8m34iuu05i">
            Partnership Opportunities
          </h1>
          <p className={`mt-2 ${dk.textSecondary}`} data-id="element-20367" data-mp="1lup9sa5pvxg7">
            Find merchandise partnership opportunities with publishers and
            authors
          </p>
        </div>

        {/* Search and Filters */}
        <div className={`rounded-lg shadow-md p-6 mb-8 border ${dk.card}`} data-id="element-20368" data-mp="2ejnthbphn7nm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-20369" data-mp="29quoqnu0rzbj">
            <div className="relative flex-1" data-id="element-20370" data-mp="24tbb9q55bqkn">
              <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-20371" data-mp="24s9q2suf1np7" />
              <input type="text" placeholder="Search opportunities by title, description, or publisher..." className={`pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-20372" data-mp="2xhgw0cw79fkh" />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center font-medium text-sm ${rt.accentLink}`} data-id="element-20373" data-mp="20u1af2soe3af">
              <FilterIcon className="h-5 w-5 mr-2" data-id="element-20374" data-mp="2tj85frsx8aqx" />
              Filters {showFilters ? '(Hide)' : '(Show)'}
            </button>
          </div>

          {showFilters && <div className={`border-t pt-4 space-y-4 ${dk.border}`} data-id="element-20375" data-mp="2fljmipiezdoi">
              <div data-id="element-20376" data-mp="2kh7aj1l5u0sf">
                <h3 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-20377" data-mp="2kgp7nm3zsobj">
                  Filter by Category
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-20378" data-mp="1933k25ds6us4">
                  {allCategories.map(category => <button key={category} onClick={() => toggleCategory(category)} className={`px-3 py-1 rounded-full text-sm border transition-all ${selectedCategories.includes(category) ? rt.badge : `${dk.badgeNeutral} ${dk.border}`}`} data-id="element-20379" data-mp="10ltdty16wc97">
                      {category}
                    </button>)}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-20380" data-mp="hw4nki8a9k06">
                <div data-id="element-20381" data-mp="cxthmil3ztn8">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20382" data-mp="cylc1q2yzlbx">
                    Project Length
                  </label>
                  <select value={projectLength} onChange={e => setProjectLength(e.target.value as ProjectLength)} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-20383" data-mp="mtda0v0gsa78">
                    <option value="Any" data-id="element-20384" data-mp="63xsgi5cya2z">Any Length</option>
                    <option value="Short-term (< 3 months)" data-id="element-20385" data-mp="62ebepbvnv05">
                      Short-term (&lt; 3 months)
                    </option>
                    <option value="Medium-term (3-6 months)" data-id="element-20386" data-mp="3sc74lcq9ghax">
                      Medium-term (3-6 months)
                    </option>
                    <option value="Long-term (6+ months)" data-id="element-20387" data-mp="3obn7ua9lnfpb">
                      Long-term (6+ months)
                    </option>
                  </select>
                </div>
                <div data-id="element-20388" data-mp="3elc3wnpx16if">
                  <label className={`block text-sm font-medium mb-1 ${dk.textSecondary}`} data-id="element-20389" data-mp="3eksj8z8vs40p">
                    Partnership Type
                  </label>
                  <select value={partnershipType} onChange={e => setPartnershipType(e.target.value as PartnershipType)} className={`w-full border rounded-md py-2 px-3 focus:outline-none focus:ring-2 ${rt.inputFocus} ${dk.inputOnCard}`} data-id="element-20390" data-mp="34kteldfl0xt4">
                    <option value="Any" data-id="element-20391" data-mp="1p2nbxauzw2hn">Any Type</option>
                    <option value="Revenue Share" data-id="element-20392" data-mp="1p3yhng803kbe">Revenue Share</option>
                    <option value="Wholesale" data-id="element-20393" data-mp="1u0x89osgh94u">Wholesale</option>
                    <option value="White Label" data-id="element-20394" data-mp="werhydwt8ook">White Label</option>
                    <option value="Licensing" data-id="element-20395" data-mp="wces7ekt9a0y">Licensing</option>
                    <option value="Event Partnership" data-id="element-20396" data-mp="11echapfw6z1t">Event Partnership</option>
                  </select>
                </div>
                <div className="flex items-center" data-id="element-20397" data-mp="16bcjuqdj9jo6">
                  <input id="featuredOnly" type="checkbox" checked={showFeaturedOnly} onChange={() => setShowFeaturedOnly(!showFeaturedOnly)} className={`h-4 w-4 border-gray-300 rounded cursor-pointer ${rt.checkbox}`} data-id="element-20398" data-mp="1b3nmi0znrjkf" />
                  <label htmlFor="featuredOnly" className={`ml-2 text-sm ${dk.textSecondary}`} data-id="element-20399" data-mp="akkg6p3d8u5a">
                    Featured Opportunities Only
                  </label>
                </div>
              </div>
            </div>}
        </div>

        {/* Opportunities */}
        <div className="space-y-6" data-id="element-20400" data-mp="3nsnge287y9g6">
          {filteredOpportunities.map(opportunity => {
          const daysRemaining = getDaysRemaining(opportunity.deadline);
          return <div key={opportunity.id} className={`rounded-lg shadow-md overflow-hidden border ${dk.card}`} data-id="element-20401" data-mp="1lp9zd0bnk1z">
                <div className="p-6" data-id="element-20402" data-mp="3sqfan2wc67nj">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4" data-id="element-20403" data-mp="1d0vpeblgqifk">
                    <div data-id="element-20404" data-mp="1821ahkqm9e0q">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-20405" data-mp="182kk8e644w3o">
                        <h2 className={`text-xl font-semibold ${dk.textPrimary}`} data-id="element-20406" data-mp="2pznt0vjikjlc">
                          {opportunity.title}
                        </h2>
                        {opportunity.featured && <span className={`text-xs px-2 py-1 rounded-full flex items-center ${rt.badge}`} data-id="element-20407" data-mp="2h0ke83rdqqcn">
                            <AwardIcon className="h-3 w-3 mr-1" data-id="element-20408" data-mp="39og4oqqcvm0z" />
                            Featured
                          </span>}
                      </div>
                      <div className="flex items-center mt-2 gap-2" data-id="element-20409" data-mp="2zydeeyamcm6c">
                        <div className="flex items-center" data-id="element-20410" data-mp="34wopfccnh2q5">
                          <img src={opportunity.publisherLogo} alt={opportunity.publisherName} className="h-8 w-8 rounded-full object-cover mr-2" data-id="element-20411" data-mp="34v3v9nu8zlst" />
                          <span className={`text-sm ${dk.textSecondary}`} data-id="element-20412" data-mp="28ro9sc6fx8t2">
                            {opportunity.publisherName}
                          </span>
                        </div>
                        <span className={dk.textFaint} data-id="element-20413" data-mp="1zq6ugw0uxb5f">•</span>
                        <span className={`text-sm flex items-center ${dk.textMuted}`} data-id="element-20414" data-mp="1zskp53rmklcg">
                          <CalendarIcon className="h-4 w-4 mr-1" data-id="element-20415" data-mp="2ri5z06s2dm9o" />
                          Posted {formatDate(opportunity.postedDate)}
                        </span>
                      </div>
                    </div>
                    <div className={`inline-flex items-center px-3 py-1 ${rt.badgeRadius} text-sm font-medium ${daysRemaining <= 7 ? 'bg-red-100 text-red-800' : daysRemaining <= 14 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`} data-id="element-20416" data-mp="2ioasovf7hpsg">
                      <ClockIcon className="h-4 w-4 mr-1" data-id="element-20417" data-mp="36en80rcs6ot2" />
                      {daysRemaining} days left
                    </div>
                  </div>

                  <p className={`mb-6 ${dk.textSecondary}`} data-id="element-20418" data-mp="3dsedh1275rdk">
                    {opportunity.description}
                  </p>

                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-20419" data-mp="3nnptdmpcd4mw">
                    {[{
                  label: 'PRODUCTION VOLUME',
                  value: opportunity.productionVolume,
                  icon: <TagIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-20420" data-mp="5immbwfteiju" />
                }, {
                  label: 'PROJECT LENGTH',
                  value: opportunity.projectLength,
                  icon: <CalendarIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-20421" data-mp="jbc6rwfp3feu" />
                }, {
                  label: 'PARTNERSHIP TYPE',
                  value: opportunity.partnershipType,
                  icon: <UsersIcon className={`h-4 w-4 mr-1 ${rt.textPrimary}`} data-id="element-20422" data-mp="2u1sbpjijrprm" />
                }, {
                  label: 'DEADLINE',
                  value: formatDate(opportunity.deadline),
                  icon: null
                }].map(({
                  label,
                  value,
                  icon
                }) => <div key={label} data-id="element-20423" data-mp="3czmrqhi3f1ji">
                        <h3 className={`text-xs font-medium mb-1 ${dk.textFaint}`} data-id="element-20424" data-mp="3cyjzo691at20">
                          {label}
                        </h3>
                        <p className={`text-sm flex items-center ${dk.textPrimary}`} data-id="element-20425" data-mp="1ioq4vg692ekn">
                          {icon}
                          {value}
                        </p>
                      </div>)}
                  </div>

                  <div className="mb-6" data-id="element-20426" data-mp="3uc36hvlcag8n">
                    <h3 className={`text-xs font-medium mb-2 ${dk.textFaint}`} data-id="element-20427" data-mp="3ghsm1qgdl88e">
                      PRODUCT CATEGORIES
                    </h3>
                    <div className="flex flex-wrap gap-1.5" data-id="element-20428" data-mp="3kickcxcoqhw4">
                      {opportunity.categories.map(category => <span key={category} className={`text-xs px-2 py-0.5 rounded-full ${dk.badgeNeutral}`} data-id="element-20429" data-mp="3bka9lq35gavn">
                          {category}
                        </span>)}
                    </div>
                  </div>

                  <div className={`border-t pt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 ${dk.border}`} data-id="element-20430" data-mp="vtxtow6ldmlw">
                    <div data-id="element-20431" data-mp="3jqs6z2jf79kz">
                      <h3 className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-20432" data-mp="3joxmtqfz3lhy">
                        Requirements
                      </h3>
                      <p className={`text-sm ${dk.textSecondary}`} data-id="element-20433" data-mp="3eticv9l369qt">
                        {opportunity.requirements}
                      </p>
                    </div>
                    <button className={`px-6 py-2 rounded-md text-sm font-medium whitespace-nowrap text-white ${rt.primaryButton}`} data-id="element-20434" data-mp="350tbj7j92c97">
                      Apply Now
                    </button>
                  </div>
                </div>
              </div>;
        })}

          {filteredOpportunities.length === 0 && <div className={`text-center py-12 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-20435" data-mp="2m3js3qdxcyry">
              <StoreIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-20436" data-mp="p9f7b7go14zs" />
              <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-20437" data-mp="kcf20yrn469q">
                No opportunities found
              </h3>
              <p className={dk.textMuted} data-id="element-20438" data-mp="sqbdok1t7jvr">
                Try adjusting your search or filters
              </p>
            </div>}
        </div>
      </div>
    </div>;
};
export default PartnershipOpportunities;